import React, { useMemo, useState } from "react";
import { Plus, Trash2 } from "lucide-react";

import NodeListReorder from "@/components/NodeListReorder";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

export default function AppointmentTableColumnsDialog({ open, onOpenChange, fields = [], value = [], onSave }) {
  const [local, setLocal] = useState(value);
  const [addKey, setAddKey] = useState("");

  React.useEffect(() => {
    if (open) {
      setLocal(Array.isArray(value) ? value : []);
      setAddKey("");
    }
  }, [open, value]);

  const labelByKey = useMemo(() => {
    const m = new Map();
    (fields || []).forEach((f) => m.set(f.key, f.label));
    return m;
  }, [fields]);

  const visibleSet = useMemo(() => new Set(local), [local]);
  const remaining = useMemo(() => (fields || []).filter((f) => !visibleSet.has(f.key)), [fields, visibleSet]);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-slate-950 text-slate-100 ring-1 ring-white/10 max-w-3xl max-h-[80vh] overflow-auto">
        <DialogHeader>
          <DialogTitle>Customize Columns</DialogTitle>
        </DialogHeader>

        <div className="space-y-4">
          <div>
            <div className="text-sm font-medium text-slate-100">Visible columns (drag to reorder)</div>
            <div className="mt-2">
              {local.length === 0 ? (
                <div className="text-sm text-slate-200/60">No columns selected.</div>
              ) : (
                <NodeListReorder items={local} getId={(k) => k} onReorder={setLocal} disabled={false}>
                  {(key) => (
                    <div className="flex items-center justify-between rounded-xl bg-black/20 px-3 py-2 ring-1 ring-white/10">
                      <div className="min-w-0">
                        <div className="truncate text-sm text-slate-100">{labelByKey.get(key) || key}</div>
                        <div className="truncate text-xs text-slate-200/60">{key}</div>
                      </div>
                      <Button
                        type="button"
                        variant="destructive"
                        size="icon"
                        onClick={() => setLocal((prev) => prev.filter((k) => k !== key))}
                        aria-label={`Remove column ${key}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  )}
                </NodeListReorder>
              )}
            </div>
          </div>

          <div className="rounded-2xl bg-black/20 p-3 ring-1 ring-white/10">
            <div className="text-sm font-medium text-slate-100">Add column</div>
            <div className="mt-2 flex flex-col gap-2 sm:flex-row sm:items-center">
              <Select value={addKey} onValueChange={setAddKey}>
                <SelectTrigger className="bg-white/5 ring-1 ring-white/10 sm:w-[320px]">
                  <SelectValue placeholder="Choose a field" />
                </SelectTrigger>
                <SelectContent className="glass border-white/10">
                  {remaining.map((f) => (
                    <SelectItem key={f.key} value={f.key}>
                      {f.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Button
                type="button"
                disabled={!addKey}
                onClick={() => {
                  if (!addKey) return;
                  setLocal((prev) => (prev.includes(addKey) ? prev : [...prev, addKey]));
                  setAddKey("");
                }}
              >
                <Plus className="mr-2 h-4 w-4" /> Add
              </Button>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button
            type="button"
            variant="secondary"
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            onClick={() => {
              onSave?.(local);
              onOpenChange(false);
            }}
          >
            Save
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
